import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { PanelLeft } from 'lucide-react'
import { navItemsFor } from '@/components/layout/nav'
import { usePermissions } from '@/hooks/usePermissions'
import { BrandMark } from '@/components/common/BrandMark'
import { useSettingsStore } from '@/store'
import { cn } from '@/lib/utils'

const STORAGE_KEY = 'mymdv.services.sidebar.collapsed'

/**
 * Is the rail collapsed?
 *
 * A device preference, not a church setting, so it lives in localStorage rather
 * than the settings store — a volunteer collapsing it on their laptop should not
 * collapse it on the office iMac. Read lazily and guarded: private browsing and
 * blocked site data both throw here, and neither is a reason to fail.
 */
function useCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0')
    } catch {
      /* nothing to do */
    }
  }, [collapsed])

  return [collapsed, setCollapsed] as const
}

/**
 * The highlight that glides between rows.
 *
 * One block per group, moved to whichever row the pointer is over, instead of
 * every row painting its own hover. The alternative blinks: two rows are briefly
 * both lit as the pointer crosses, and nothing connects them.
 *
 * Positioned with transform and sized with height — both compositor properties —
 * so it slides without laying anything out again.
 */
function useGlide<T extends HTMLElement>() {
  const group = useRef<T>(null)
  const [box, setBox] = useState<{ top: number; height: number } | null>(null)

  const track = useCallback((event: React.PointerEvent | React.FocusEvent) => {
    const row = (event.target as Element).closest<HTMLElement>('[data-row]')
    if (row && group.current?.contains(row)) {
      // offsetTop is relative to the positioned group, so this stays correct
      // however far the nav has been scrolled.
      setBox({ top: row.offsetTop, height: row.offsetHeight })
    }
  }, [])

  const clear = useCallback(() => setBox(null), [])

  return { group, box, track, clear }
}

function NavList({ collapsed }: { collapsed: boolean }) {
  const { role } = usePermissions()
  const { group, box, track, clear } = useGlide<HTMLElement>()

  return (
    <nav
      ref={group}
      onPointerOver={track}
      onPointerLeave={clear}
      onFocus={track}
      onBlur={clear}
      className="relative flex-1 space-y-px overflow-y-auto p-2 scrollbar-thin"
      aria-label="Main"
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-2 top-0 rounded-lg bg-muted transition-[transform,height,opacity]',
          'duration-[180ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
          box ? 'opacity-100' : 'opacity-0',
        )}
        style={box ? { height: box.height, transform: `translateY(${box.top}px)` } : undefined}
      />

      {navItemsFor(role).map(({ to, label, icon: Icon, end, description }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-row
          title={`${label} — ${description}`}
          className={({ isActive }) =>
            cn(
              'relative z-10 flex h-9 items-center gap-2.5 rounded-lg px-2 text-sm transition-colors',
              isActive
                ? 'bg-accent font-medium text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )
          }
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
            <Icon className="h-4 w-4" aria-hidden />
          </span>
          {/*
            Never unmounted when collapsed — only faded. Removing it would let
            the row re-lay-out mid-animation and the icons would shift, which is
            the whole thing the fixed inner column exists to prevent.
          */}
          <span
            className={cn(
              'min-w-0 flex-1 truncate transition-[opacity,transform] duration-[180ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
              collapsed && 'pointer-events-none -translate-x-2 opacity-0',
            )}
          >
            {label}
          </span>
        </NavLink>
      ))}
    </nav>
  )
}

function Brand({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const organizationName = useSettingsStore((s) => s.settings?.organizationName)

  return (
    <div className="relative h-14 shrink-0 border-b border-border">
      <div
        className={cn(
          'absolute left-3 top-3 flex h-8 items-center gap-2.5 transition-[opacity,transform] duration-[180ms]',
          collapsed && 'pointer-events-none -translate-x-2 opacity-0',
        )}
      >
        <BrandMark size="sm" />
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold leading-tight">MDV Services</span>
          <span className="block truncate text-[11px] leading-tight text-muted-foreground">
            {organizationName ?? 'MDV Miami'}
          </span>
        </span>
      </div>

      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
        className={cn(
          'absolute top-3 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground',
          'transition-[left,right,background-color,color] duration-[180ms] hover:bg-muted hover:text-foreground',
          collapsed ? 'left-3' : 'right-2',
        )}
      >
        <PanelLeft className={cn('h-[18px] w-[18px]', collapsed && 'rotate-180')} aria-hidden />
      </button>
    </div>
  )
}

/**
 * Desktop navigation rail.
 *
 * Nothing here below `lg` — mobile uses the bottom tab bar alone, so there is
 * no drawer and no hamburger to open one.
 *
 * The rail collapses to icons. The inner column is a fixed 224px and never
 * reflows; collapsing narrows only the <aside> that clips it. Animating the
 * column's own width instead re-wraps every label on each frame, which is what
 * makes most collapsing sidebars judder.
 */
export function SideNav() {
  const [collapsed, setCollapsed] = useCollapsed()

  return (
    <aside
      data-collapsed={collapsed || undefined}
      className={cn(
        'hidden shrink-0 overflow-hidden border-r border-border bg-card lg:flex no-print',
        // Pinned, not scrolled. AppShell's row is min-h-screen so it grows with
        // the page, which leaves <main>'s overflow-y-auto inert and makes the
        // document the scroller — which used to carry the sidebar away with it.
        // self-start matters: a stretched flex item is already the row's height
        // and cannot stick.
        'lg:sticky lg:top-0 lg:h-screen lg:self-start',
        'transition-[width] duration-[280ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
        collapsed ? 'w-[3.25rem]' : 'w-56',
      )}
    >
      <div className="flex min-h-0 w-56 shrink-0 flex-col">
        <Brand collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
        <NavList collapsed={collapsed} />
        <p
          className={cn(
            'border-t border-border px-4 py-3 text-[11px] text-muted-foreground',
            'transition-opacity duration-[180ms]',
            collapsed && 'opacity-0',
          )}
        >
          MDV Services
        </p>
      </div>
    </aside>
  )
}
